/**
 * AggregationConfig
 *
 * UI for configuring aggregations in report view.
 * Shows aggregatable fields (numeric) with sum/avg options,
 * and allows count for any field.
 *
 * All field options are fetched from Frappe meta - nothing hardcoded.
 */

import { useState } from "react";
import { useReportMeta } from "../../api/useReportMeta";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Calculator, Plus, X } from "lucide-react";
import type { AggregationType } from "@/lib/erp/reports";

interface AggregationConfigProps {
  doctype: string;
  value: Record<string, AggregationType>;
  onChange: (aggregations: Record<string, AggregationType>) => void;
  disabled?: boolean;
}

export function AggregationConfig({
  doctype,
  value,
  onChange,
  disabled,
}: AggregationConfigProps) {
  const { data: meta, isLoading } = useReportMeta(doctype);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<string>("");
  const [selectedAggType, setSelectedAggType] = useState<AggregationType>("sum");

  if (isLoading) {
    return (
      <Button variant="outline" size="sm" disabled className="h-10">
        <Calculator className="h-4 w-4 mr-2" />
        Loading...
      </Button>
    );
  }

  const numericFields = meta?.aggregatable_fields ?? [];
  const countableFields = meta?.countable_fields ?? [];

  // Get available fields (not already selected)
  const selectedFieldnames = new Set(Object.keys(value));
  const availableNumericFields = numericFields.filter(
    (f) => !selectedFieldnames.has(f.fieldname)
  );
  const availableCountableFields = countableFields.filter(
    (f) => !selectedFieldnames.has(f.fieldname)
  );

  // Determine available agg types based on selected field
  const getAvailableAggTypes = (fieldname: string): AggregationType[] => {
    const isNumeric = numericFields.some((f) => f.fieldname === fieldname);
    if (isNumeric) {
      return ["sum", "avg", "count"];
    }
    return ["count"];
  };

  const handleAddAggregation = () => {
    if (!selectedField) return;

    onChange({
      ...value,
      [selectedField]: selectedAggType,
    });

    setSelectedField("");
    setSelectedAggType("sum");
  };

  const handleRemoveAggregation = (fieldname: string) => {
    const newValue = { ...value };
    delete newValue[fieldname];
    onChange(newValue);
  };

  const getFieldLabel = (fieldname: string): string => {
    const field = countableFields.find((f) => f.fieldname === fieldname);
    return field?.label || fieldname;
  };

  const getAggLabel = (aggType: AggregationType): string => {
    switch (aggType) {
      case "sum":
        return "Sum";
      case "avg":
        return "Avg";
      case "count":
        return "Count";
      default:
        return aggType;
    }
  };

  const aggregationCount = Object.keys(value).length;

  return (
    <div className="flex items-center gap-2">
      {/* Display current aggregations as badges */}
      {Object.entries(value).map(([fieldname, aggType]) => (
        <Badge
          key={fieldname}
          variant="secondary"
          className="flex items-center gap-1 h-8 px-2"
        >
          <span className="text-xs font-medium">
            {getAggLabel(aggType)}({getFieldLabel(fieldname)})
          </span>
          <button
            onClick={() => handleRemoveAggregation(fieldname)}
            className="ml-1 hover:bg-muted rounded-[2px] p-0.5"
            disabled={disabled}
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}

      {/* Add aggregation popover */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={disabled || availableCountableFields.length === 0}
            className="h-10"
          >
            <Calculator className="h-4 w-4 mr-2" />
            {aggregationCount === 0 ? "Add Measure" : <Plus className="h-4 w-4" />}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="start">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Field</label>
              <Select value={selectedField} onValueChange={setSelectedField}>
                <SelectTrigger>
                  <SelectValue placeholder="Select field..." />
                </SelectTrigger>
                <SelectContent>
                  {availableNumericFields.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                        Numeric Fields (Sum, Avg, Count)
                      </div>
                      {availableNumericFields.map((field) => (
                        <SelectItem key={field.fieldname} value={field.fieldname}>
                          {field.label}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {availableCountableFields.length > availableNumericFields.length && (
                    <>
                      <div className="px-2 py-1 text-xs font-semibold text-muted-foreground mt-2">
                        Other Fields (Count only)
                      </div>
                      {availableCountableFields
                        .filter(
                          (f) =>
                            !availableNumericFields.some(
                              (nf) => nf.fieldname === f.fieldname
                            )
                        )
                        .map((field) => (
                          <SelectItem key={field.fieldname} value={field.fieldname}>
                            {field.label}
                          </SelectItem>
                        ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {selectedField && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Function</label>
                <Select
                  value={selectedAggType}
                  onValueChange={(v) => setSelectedAggType(v as AggregationType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableAggTypes(selectedField).map((aggType) => (
                      <SelectItem key={aggType} value={aggType}>
                        {getAggLabel(aggType)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button
              onClick={handleAddAggregation}
              disabled={!selectedField}
              className="w-full"
            >
              Add Measure
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
